import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigation } from '../contexts/NavigationContext';
import { useNavigate } from 'react-router-dom';
import NavigationModal from './NavigationModal';
import { DiscountApprovalModal } from './ui';
import { QuickAddExpense } from './Expenses';
import { ShoppingCart, LogOut, Store, Menu, BarChart3, Package, RefreshCw } from 'lucide-react';
import { config } from '../config/env';
import { useAppStore } from '../stores/useAppStore';
import { can, PERMS } from '../utils/accessControl';

const Layout = ({ children }) => {
  const appIcon = config.appIcon;
  const { user, logout, isAuthenticated } = useAuth();
  const { isNavigationModalOpen, openNavigationModal, closeNavigationModal } = useNavigation();
  const { businessInfo } = useAppStore();
  const navigate = useNavigate();

  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const canAddExpense = can(user, PERMS.EXPENSES);

  // Global Ctrl+E listener for quick expense add (roles with the Expenses page)
  useEffect(() => {
    if (!canAddExpense) return undefined;
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 'e') {
        e.preventDefault();
        setIsExpenseModalOpen(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [canAddExpense]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };


  if (!isAuthenticated) {
    return <>{children}</>;
  }


  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header: the dark-wash denim bar of the sign-in page, with the shop's logo
          sewn on as a white label so black and colourful logos both show. */}
      <header className="flex-shrink-0 bg-blue-900 text-white">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center gap-3 h-16">
            {/* Left side - Navigation button, Logo and Mobile menu */}
            <div className="flex min-w-0 items-center gap-3">
              {/* Navigation Button - Hidden on mobile */}
              <button
                onClick={openNavigationModal}
                className="relative hidden md:flex w-10 h-10 flex-shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                title="Open Navigation (Ctrl+M)"
                aria-label="Open navigation (Ctrl+M)"
              >
                <Menu className="h-5 w-5" />
                {/* Current page indicator */}
                <div className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-blue-900 bg-blue-300"></div>
              </button>

              {/* Mobile menu button - Opens navigation modal directly */}
              <button
                onClick={openNavigationModal}
                className="md:hidden flex-shrink-0 p-2 rounded-md text-white hover:bg-white/10"
                aria-label="Open navigation"
              >
                <Menu className="h-6 w-6" />
              </button>

              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-10 flex-shrink-0 items-center rounded-md bg-white px-1.5">
                  <img src={appIcon} alt="" className="h-8 w-auto max-w-[72px] object-contain" />
                </span>
                <div className="min-w-0 leading-tight">
                  <h1 className="truncate text-base font-semibold sm:text-lg">
                    {businessInfo?.BusinessName || config.appTitle}
                  </h1>
                  {businessInfo?.BranchLocation?.trim() && (
                    <p className="truncate text-sm text-blue-200">{businessInfo.BranchLocation.trim()} branch</p>
                  )}
                </div>
              </div>
            </div>

            {/* Right side - User info */}
            <div className="flex flex-shrink-0 items-center gap-2">
              <div className="flex items-center gap-2 rounded-full bg-white/10 py-1 pl-1 pr-1 sm:pr-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-blue-900">
                  <span className="text-sm font-semibold">
                    {user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'U'}
                  </span>
                </div>
                <span className="hidden text-sm font-medium sm:block">
                  {user?.full_name || user?.username}
                </span>
              </div>

              <button
                onClick={handleLogout}
                className="flex items-center gap-2 rounded-lg px-3 py-2 text-blue-100 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:block text-sm font-medium">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>


      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {children}
        </div>
      </main>

      {/* Navigation Modal */}
      <NavigationModal 
        isOpen={isNavigationModalOpen} 
        onClose={closeNavigationModal}
      />

      {/* Discount Approval Modal */}
      <DiscountApprovalModal />

      {/* Quick Add Expense Modal */}
      <QuickAddExpense
        isOpen={isExpenseModalOpen}
        onClose={() => setIsExpenseModalOpen(false)}
      />
    </div>
  );
};

export default Layout;
